import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Download, FileText, Image as ImageIcon, Sparkles, Plus } from "lucide-react";
import { api, formatErr } from "../../api";

/**
 * Daily-Expense Receipts — sits inside Products & Inventory as the 6th tab.
 *
 * Flow:
 *  1. Admin drops a receipt (image / PDF) into the top drop-zone.
 *  2. Frontend POSTs to /api/mgmt/expenses/scan; GPT-5.2 Vision returns
 *     {date, total, merchant, tax}.
 *  3. A "Confirm & save" modal shows the extracted values with editable
 *     category (Misc / Meals / free-text) + notes. Admin clicks Save →
 *     POST /api/mgmt/expenses (multipart) which stores the file in
 *     Emergent Object Storage and inserts a row in Mongo.
 *  4. Table below lists everything for the selected month, sorted by
 *     date desc. Checkboxes + a Batch Download button stream a ZIP
 *     with files renamed `<YYYY-MM-DD>_<merchant>_<amount>.<ext>`.
 *
 * Everything an admin uploads immediately flows into the Monthly P&L
 * `receipts` bucket + `Operating Cost` total via backend hooks in
 * management.py — no manual re-entry required.
 */
const inputCls = "rounded-sm border border-ink/15 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-matcha-dark";
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORIES = [
  "Misc Expenses",
  "Meals and Entertainment Expense",
  "Travel",
  "Repair & Maintenance",
  "Cleaning",
  "Marketing",
  "Office Supplies",
];

export default function ExpensesTab() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  // Multi-file queue: [{ id, file, scanned, error }, ...]. Modal walks
  // through the queue one at a time so admins can Save / Skip each row
  // without losing the AI-extracted values on the ones still waiting.
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);
  const manualFileRef = useRef(null);

  // Manual entry — bypass the AI scan entirely and jump straight to the
  // confirm modal with empty fields. Useful when GPT-5.2 can't read a
  // scrappy handwritten receipt or the admin just wants to log an amount
  // in a hurry. Same file + storage path as the AI flow so the row still
  // shows up in ZIP downloads and P&L.
  const handleManualFile = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const entries = files.slice(0, 10).map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      scanned: null,
      manual: true,   // flag ConfirmModal to show the friendlier "manual entry" copy
    }));
    setQueue((prev) => [...prev, ...entries]);
    if (manualFileRef.current) manualFileRef.current.value = "";
  };

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/mgmt/expenses?month=${month}`);
      setRows(data || []);
      // Clear any selections that no longer exist after a re-fetch.
      setSelected((prev) => {
        const ids = new Set((data || []).map((r) => r.id));
        return new Set([...prev].filter((id) => ids.has(id)));
      });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  // Multi-file OCR — accepts up to 10 files at once (matches the backend's
  // PDF page cap). Each file is scanned INDEPENDENTLY so one unreadable
  // receipt doesn't block the rest. Failures land in the queue with an
  // `error` field so admins can still see which one flopped and re-drop it.
  const MAX_BATCH = 10;
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setErr("");
    // Guardrail: reject files over 20 MB up front so admins see a clear
    // "too big" message instead of a mysterious 413 later in the flow.
    const OVERSIZED = files.filter((f) => f.size > 20 * 1024 * 1024);
    if (OVERSIZED.length) {
      setErr(`${OVERSIZED.length} file${OVERSIZED.length === 1 ? "" : "s"} exceed 20 MB and will be skipped: ${OVERSIZED.map((f) => f.name).join(", ")}`);
    }
    const usable = files.filter((f) => f.size <= 20 * 1024 * 1024);
    if (usable.length === 0) return;
    if (usable.length > MAX_BATCH) {
      setErr((e) => `${e}${e ? " · " : ""}Received ${usable.length} files — first ${MAX_BATCH} will be scanned.`);
    }
    const batch = usable.slice(0, MAX_BATCH);
    setScanning(true);
    setScanProgress({ done: 0, total: batch.length });

    // Parallel scans, capped at 3 in flight at a time — otherwise 10 large
    // PDFs saturate the backend's event loop (pypdfium2 render is CPU-bound)
    // and cause the "very inconsistent" random timeouts. A small worker-pool
    // pattern here keeps throughput high without overloading the pod.
    const CONCURRENCY = 3;
    const results = new Array(batch.length);
    let cursor = 0;
    const worker = async () => {
      while (true) {
        const i = cursor++;
        if (i >= batch.length) return;
        const f = batch[i];
        try {
          const fd = new FormData();
          fd.append("file", f);
          const { data } = await api.post("/mgmt/expenses/scan", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            // 90s per scan — GPT-5.2 usually returns in 2-5s, but the pod
            // occasionally queues behind a big PDF render. Anything over 90s
            // is a real failure worth surfacing.
            timeout: 90_000,
          });
          setScanProgress((p) => ({ ...p, done: p.done + 1 }));
          results[i] = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f, scanned: data };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[expenses] scan failed for", f?.name, e?.response?.status, e?.response?.data || e);
          setScanProgress((p) => ({ ...p, done: p.done + 1 }));
          results[i] = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file: f,
            scanned: null,
            error: formatErr(e?.response?.data?.detail) || e.message || "Scan failed",
          };
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batch.length) }, worker));

    // Append instead of replacing so an admin who drops a 2nd batch mid-review
    // extends the queue rather than losing the first batch's progress.
    setQueue((prev) => [...prev, ...results]);
    setScanning(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Delete this receipt from the P&L?")) return;
    try {
      await api.delete(`/mgmt/expenses/${id}`);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  // Toggle a single row; "select all" flips the entire visible-month set.
  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length && rows.length > 0) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  // Batch ZIP download. We stream the response as a blob so the browser
  // pops the native "Save as" dialog with the filename set by the backend.
  const downloadBatch = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    try {
      const resp = await api.post(
        "/mgmt/expenses/download",
        { ids: Array.from(selected) },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `expenses-${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setDownloading(false);
    }
  };

  const total = rows.reduce((acc, r) => acc + Number(r.amount || 0), 0);
  const byCat = rows.reduce((acc, r) => {
    const c = r.category || "Misc Expenses";
    acc[c] = (acc[c] || 0) + Number(r.amount || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="expenses-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">— Daily Expenses</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Petty cash, meals & misc — scanned into the P&L
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-2xl">
            Drop any receipt (image or PDF). Our AI auto-extracts <strong className="text-ink">date · total · merchant · tax</strong>,
            then stores the file so you can download a ZIP anytime. Everything you save here flows into the
            <strong className="text-ink"> Operating Cost</strong> total on the Monthly P&L instantly.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="overline text-ink-soft">Month</label>
          <input
            type="month"
            data-testid="expenses-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {err && (
        <div data-testid="expenses-err" className="rounded-sm border border-red-700/30 bg-red-50 text-red-900 text-sm px-3 py-2">
          {err}
        </div>
      )}

      {/* Drop zone + manual button — the manual button is right next to
          (actually stacked on top of, mobile-first) the drop zone so admins
          always have a one-click bypass when the AI can't read a receipt. */}
      <div className="relative">
        <div
          data-testid="expenses-drop"
          className="rounded-sm border-2 border-dashed border-matcha-dark/40 bg-cream/40 hover:bg-white p-8 text-center cursor-pointer transition-colors"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        >
          <div className="inline-flex items-center gap-2 text-matcha-dark">
            <Sparkles size={20} />
            <Upload size={22} />
          </div>
          <p className="mt-3 text-ink">
            <strong>Drop up to {MAX_BATCH} receipts (images / PDFs) here</strong> or click to browse
          </p>
          <p className="text-ink-soft text-xs mt-1">
            AI reads date · total · merchant · tax on each one · then review & save one by one · max 20 MB each
          </p>
          <input
            ref={fileRef}
            type="file"
            data-testid="expenses-file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {scanning && (
            <p className="mt-3 inline-flex items-center gap-2 text-matcha-dark" data-testid="expenses-scan-progress">
              <Loader2 className="animate-spin" size={16} />
              Reading receipt {scanProgress.done + 1} of {scanProgress.total} with GPT-5.2 Vision…
            </p>
          )}
          {!scanning && queue.length > 0 && (
            <p className="mt-3 inline-flex items-center gap-2 text-ink" data-testid="expenses-queue-indicator">
              <FileText size={14} />
              <strong>{queue.length}</strong> receipt{queue.length === 1 ? "" : "s"} awaiting review — save or skip each below.
            </p>
          )}
        </div>

        {/* Manual button pinned to the top-right corner of the drop zone.
            `stopPropagation` prevents the drop zone's own click handler
            from firing when the button is tapped. */}
        <button
          type="button"
          data-testid="expenses-add-manual-btn"
          onClick={(e) => { e.stopPropagation(); manualFileRef.current?.click(); }}
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-white border border-ink/15 text-[11px] uppercase tracking-[0.18em] text-ink hover:bg-ink hover:text-bone transition-colors"
          title="Skip AI · enter fields manually"
        >
          <Plus size={12} /> Add manually
        </button>
        <input
          ref={manualFileRef}
          type="file"
          data-testid="expenses-manual-file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleManualFile(e.target.files)}
        />
      </div>

      {/* Totals ribbon */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total this month"  value={`RM ${money(total)}`} testid="expenses-total" />
        <Kpi label="Receipts"          value={`${rows.length}`}      sub="Filed for the month" />
        <Kpi label="Selected"          value={`${selected.size}`}    sub={selected.size > 0 ? `RM ${money(rows.filter((r) => selected.has(r.id)).reduce((a, r) => a + Number(r.amount || 0), 0))} · ready to zip` : "Tick rows to include in ZIP"} />
        <Kpi label="Top category"      value={Object.entries(byCat).sort((a,b) => b[1] - a[1])[0]?.[0] || "—"}
             sub={Object.entries(byCat).sort((a,b) => b[1] - a[1])[0] ? `RM ${money(Object.entries(byCat).sort((a,b) => b[1] - a[1])[0][1])}` : ""} />
      </div>

      {/* Table */}
      <div className="rounded-sm border border-ink/10 bg-white/60 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink/10 flex-wrap">
          <p className="overline text-ink-soft">Filed receipts · {month}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={toggleAll}
              data-testid="expenses-select-all"
              disabled={rows.length === 0}
              className="text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink disabled:opacity-40"
            >
              {selected.size === rows.length && rows.length > 0 ? "Deselect all" : "Select all"}
            </button>
            <button
              type="button"
              onClick={downloadBatch}
              data-testid="expenses-download-btn"
              disabled={selected.size === 0 || downloading}
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-ink text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-matcha-dark disabled:opacity-40 transition-colors"
            >
              {downloading ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />}
              Download ZIP ({selected.size})
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="text-left text-ink-soft border-b border-ink/10">
              <tr>
                <th className="pl-4 pr-2 py-2 w-8"></th>
                <th className="px-2 py-2 overline">Date</th>
                <th className="px-2 py-2 overline">Merchant</th>
                <th className="px-2 py-2 overline">Category</th>
                <th className="px-2 py-2 overline text-right">Amount</th>
                <th className="px-2 py-2 overline text-right">Tax</th>
                <th className="px-2 py-2 overline">File</th>
                <th className="px-2 py-2 overline w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="py-8 text-center text-ink-soft"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-ink-soft">
                  No receipts filed for {month}. Drop one above to get started.
                </td></tr>
              )}
              {!loading && rows.map((r) => {
                const isPdf = (r.content_type || "").includes("pdf") || (r.filename || "").toLowerCase().endsWith(".pdf");
                return (
                  <tr key={r.id} className="border-t border-ink/5 hover:bg-cream/20" data-testid={`expenses-row-${r.id}`}>
                    <td className="pl-4 pr-2 py-2">
                      <input
                        type="checkbox"
                        data-testid={`expenses-check-${r.id}`}
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                        className="accent-matcha-dark"
                      />
                    </td>
                    <td className="px-2 py-2 text-ink whitespace-nowrap">{r.date}</td>
                    <td className="px-2 py-2 text-ink truncate max-w-[14rem]" title={r.merchant}>{r.merchant || "—"}</td>
                    <td className="px-2 py-2 text-ink-soft truncate max-w-[16rem]" title={r.category}>{r.category}</td>
                    <td className="px-2 py-2 text-right text-ink tabular-nums whitespace-nowrap">RM {money(r.amount)}</td>
                    <td className="px-2 py-2 text-right text-ink-soft tabular-nums whitespace-nowrap">
                      {r.tax_amount != null ? `RM ${money(r.tax_amount)}` : "—"}
                    </td>
                    <td className="px-2 py-2">
                      <FileLink id={r.id} label={r.filename} isPdf={isPdf} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => del(r.id)}
                        data-testid={`expenses-del-${r.id}`}
                        title="Delete"
                        className="text-ink-soft hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Walk through the queue one receipt at a time so admin can Save or
          Skip each before moving on. Modal disappears when queue is empty. */}
      {queue.length > 0 && (
        <ConfirmModal
          key={queue[0].id}
          entry={queue[0]}
          index={0}
          total={queue.length}
          onSkip={() => setQueue((q) => q.slice(1))}
          onCancelAll={() => setQueue([])}
          onSaveError={(msg) => setErr(msg)}
          onSaved={async () => {
            setQueue((q) => q.slice(1));
            try {
              await load();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("[expenses] reload after save failed", e);
            }
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="border border-ink/10 rounded-sm bg-cream/40 p-3" data-testid={testid}>
      <p className="overline text-ink-soft">{label}</p>
      <p className="text-ink text-lg mt-1 truncate" title={value}>{value}</p>
      {sub && <p className="text-[11px] text-ink-soft mt-1 truncate">{sub}</p>}
    </div>
  );
}

// Auth'd file link — API sits behind Bearer JWT, so <a href> won't work
// directly. Fetch as blob, open in a new tab. Cheap enough for one-click views.
function FileLink({ id, label, isPdf }) {
  const [busy, setBusy] = useState(false);
  const view = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/mgmt/expenses/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank", "noopener");
      // Revoke slightly later so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={view}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-matcha-dark hover:text-ink"
      title={label}
    >
      {isPdf ? <FileText size={12} /> : <ImageIcon size={12} />}
      {busy ? "…" : "View"}
    </button>
  );
}

function ConfirmModal({ entry, index, total, onSkip, onCancelAll, onSaved, onSaveError }) {
  const { file, scanned, error, manual } = entry;
  const [form, setForm] = useState({
    date: scanned?.date || new Date().toISOString().slice(0, 10),
    amount: scanned?.total_amount != null ? String(scanned.total_amount) : "",
    category: "Misc Expenses",
    merchant: scanned?.merchant || "",
    tax_amount: scanned?.tax_amount != null ? String(scanned.tax_amount) : "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(error || "");

  const missing = !scanned?.date || scanned?.total_amount == null;

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    // Client-side validation with the SAME rules the backend enforces so the
    // user gets an immediate red banner instead of a mystery 400.
    const amt = Number(form.amount);
    if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      setErr(`Date is required (YYYY-MM-DD). Got: ${form.date || "(empty)"}`);
      return;
    }
    if (!form.amount || Number.isNaN(amt) || amt <= 0) {
      setErr(`Total amount must be a number greater than zero. Got: ${form.amount || "(empty)"}`);
      return;
    }
    if (!file || !file.name) {
      setErr("File attachment missing — please re-drop the receipt.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("date", form.date);
      fd.append("amount", String(amt));
      fd.append("category", form.category || "Misc Expenses");
      fd.append("merchant", form.merchant || "");
      if (form.tax_amount !== "" && !Number.isNaN(Number(form.tax_amount))) {
        fd.append("tax_amount", String(Number(form.tax_amount)));
      }
      fd.append("notes", form.notes || "");
      // eslint-disable-next-line no-console
      console.log("[expenses] saving", file.name, {
        size: file.size, date: form.date, amount: amt, category: form.category,
      });
      await api.post("/mgmt/expenses", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // 60s cap — file upload can be slow on mobile networks but anything
        // longer means the storage layer is likely failing.
        timeout: 60_000,
      });
      // eslint-disable-next-line no-console
      console.log("[expenses] saved OK", file.name);
      onSaved();
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error("[expenses] save failed for", file?.name, e2?.response?.status, e2?.response?.data || e2);
      // Surface HTTP status + backend detail so the admin sees the ACTUAL
      // reason instead of a generic "save failed" and can screenshot it.
      const status = e2?.response?.status ? `[${e2.response.status}] ` : "";
      const detail = formatErr(e2?.response?.data?.detail) || e2?.message || "Unknown error";
      const msg = `Failed to save "${file?.name || "receipt"}" · ${status}${detail}`;
      setErr(msg);
      onSaveError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="expenses-confirm-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
            Confirm receipt
          </h3>
          {total > 1 && (
            <span
              data-testid="expenses-modal-progress"
              className="text-[11px] uppercase tracking-[0.18em] text-matcha-dark bg-matcha-dark/10 px-3 py-1 rounded-full"
            >
              {index + 1} of {total} · {total - 1 - index} left after this
            </span>
          )}
        </div>
        <p className="text-ink-soft text-xs mb-6 truncate" title={file?.name}>
          {file?.name}
          {" · "}
          {error ? (
            <span className="text-red-700">AI failed — enter the fields manually.</span>
          ) : manual ? (
            <span className="text-matcha-dark">Manual entry — no AI scan. Fill in every field below.</span>
          ) : missing ? (
            <span className="text-amber-800">
              AI couldn't read one or more fields — fill in the missing bits before saving.
            </span>
          ) : (
            <>Extracted by GPT-5.2 Vision. Adjust anything before saving.</>
          )}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="overline text-ink-soft block mb-2">Date</label>
            <input required type="date" className={inputCls + " w-full"} value={form.date}
                   data-testid="expenses-form-date"
                   onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Total (RM)</label>
            <input required type="number" step="0.01" min="0" className={inputCls + " w-full text-right tabular-nums"} value={form.amount}
                   data-testid="expenses-form-amount"
                   onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="overline text-ink-soft block mb-2">Category</label>
            <input list="expense-categories" className={inputCls + " w-full"} value={form.category}
                   data-testid="expenses-form-category"
                   onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} />
            <datalist id="expense-categories">
              {CATEGORIES.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="col-span-2">
            <label className="overline text-ink-soft block mb-2">Merchant</label>
            <input className={inputCls + " w-full"} value={form.merchant}
                   data-testid="expenses-form-merchant"
                   onChange={(e) => setForm((s) => ({ ...s, merchant: e.target.value }))} />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Tax (RM, optional)</label>
            <input type="number" step="0.01" min="0" className={inputCls + " w-full text-right tabular-nums"} value={form.tax_amount}
                   onChange={(e) => setForm((s) => ({ ...s, tax_amount: e.target.value }))} />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">File</label>
            <p className="text-ink text-sm truncate py-2" title={file?.name}>{file?.name || "—"}</p>
          </div>
          <div className="col-span-2">
            <label className="overline text-ink-soft block mb-2">Notes (optional)</label>
            <textarea rows={2} className={inputCls + " w-full"} value={form.notes}
                      onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          </div>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-6" data-testid="expenses-form-err">{err}</p>}
        <div className="flex justify-end gap-3 mt-8 items-center flex-wrap">
          {total > 1 && (
            <button
              type="button"
              onClick={onCancelAll}
              data-testid="expenses-form-cancel-all"
              className="text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-red-700 mr-auto"
              title="Discard the whole queue"
            >
              Cancel all ({total})
            </button>
          )}
          <button
            type="button"
            onClick={onSkip}
            data-testid="expenses-form-skip"
            className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
            title="Skip this receipt and move to the next"
          >
            {total > 1 ? "Skip this" : "Cancel"}
          </button>
          <button
            data-testid="expenses-form-save"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-60"
          >
            {saving && <Loader2 className="animate-spin" size={12} />}
            {total > 1 ? "Save & next" : "Save receipt"}
          </button>
        </div>
      </form>
    </div>
  );
}
